import JSZip from 'jszip';
import { generatePdf } from '@/reports/registry.js';

// generatePdf returns a blob: object URL (the live preview consumes it as an
// <a href>). For file writing we need the actual bytes - read the blob back and
// release the URL. Tolerates a future bytes-returning generatePdf.
async function renderPdfBytes(reportType, data, logos) {
  const result = await generatePdf(reportType, data, { logos });
  if (typeof result !== 'string') return result;
  const buf = await (await fetch(result)).arrayBuffer();
  URL.revokeObjectURL(result);
  return new Uint8Array(buf);
}

function sanitizeFilename(name) {
  return String(name)
    .replace(/[^a-z0-9_\-.]/gi, '_')
    .replace(/_{2,}/g, '_')
    .replace(/^_|_$/g, '')
    || 'report';
}

function buildCsv(reports) {
  const allKeys = new Set();
  reports.forEach((r) => {
    if (r.data && typeof r.data === 'object') {
      Object.keys(r.data).forEach((k) => allKeys.add(k));
    }
  });

  const dataKeys = Array.from(allKeys).sort();
  const headers = ['studentName', 'reportType', ...dataKeys];

  const escape = (val) => {
    const s = val == null ? '' : String(val);
    if (s.includes(',') || s.includes('"') || s.includes('\n')) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };

  const rows = [headers.map(escape).join(',')];
  reports.forEach((r) => {
    const row = [
      r.studentName ?? '',
      r.reportType ?? '',
      ...dataKeys.map((k) => r.data?.[k] ?? ''),
    ].map(escape);
    rows.push(row.join(','));
  });

  return rows.join('\n');
}

/**
 * Export selected reports as PDFs and/or a CSV.
 *
 * Uses the File System Access API when available (Chrome/Edge): opens a folder
 * picker, creates a subfolder named after the Report Folder, writes each file there.
 *
 * Falls back to a JSZip download for Firefox/Safari.
 *
 * @param {object} opts
 * @param {Array}   opts.reports          - Report objects with { _id, studentName, reportType, data }
 * @param {boolean} opts.exportPdfs       - Whether to generate PDFs
 * @param {boolean} opts.exportCsv        - Whether to generate a CSV
 * @param {string}  opts.reportFolderName - Used as subfolder / ZIP name
 * @param {(done: number, total: number) => void} opts.onProgress
 * @returns {Promise<boolean>} false if user cancelled, true on success
 */
function buildFilenames(reports) {
  const counts = {};
  reports.forEach((r) => {
    const base = sanitizeFilename(r.studentName);
    counts[base] = (counts[base] || 0) + 1;
  });
  const seen = {};
  return reports.map((r) => {
    const base = sanitizeFilename(r.studentName);
    if (counts[base] > 1) {
      seen[base] = (seen[base] || 0) + 1;
      return `${base}_${seen[base]}`;
    }
    return base;
  });
}

export async function bulkExport({ reports, exportPdfs, exportCsv, reportFolderName, logos = [], onProgress }) {
  const folderName = sanitizeFilename(reportFolderName || 'report_folder_export');
  const total = (exportPdfs ? reports.length : 0) + (exportCsv ? 1 : 0);
  let done = 0;
  const filenames = buildFilenames(reports);

  if ('showDirectoryPicker' in window) {
    let dirHandle;
    try {
      dirHandle = await window.showDirectoryPicker({ mode: 'readwrite', startIn: 'documents' });
    } catch {
      return false; // user cancelled picker
    }

    const subDir = await dirHandle.getDirectoryHandle(folderName, { create: true });

    if (exportPdfs) {
      for (let i = 0; i < reports.length; i++) {
        const report = reports[i];
        const pdfBytes = await renderPdfBytes(report.reportType, report.data || {}, logos);
        const fh = await subDir.getFileHandle(filenames[i] + '.pdf', { create: true });
        const writable = await fh.createWritable();
        await writable.write(pdfBytes);
        await writable.close();
        done++;
        onProgress(done, total);
      }
    }

    if (exportCsv) {
      const csv = buildCsv(reports);
      const fh = await subDir.getFileHandle('export.csv', { create: true });
      const writable = await fh.createWritable();
      await writable.write(csv);
      await writable.close();
      done++;
      onProgress(done, total);
    }
  } else {
    // JSZip fallback for browsers without File System Access API
    const zip = new JSZip();
    const folder = zip.folder(folderName);

    if (exportPdfs) {
      for (let i = 0; i < reports.length; i++) {
        const report = reports[i];
        const pdfBytes = await renderPdfBytes(report.reportType, report.data || {}, logos);
        folder.file(filenames[i] + '.pdf', pdfBytes);
        done++;
        onProgress(done, total);
      }
    }

    if (exportCsv) {
      folder.file('export.csv', buildCsv(reports));
      done++;
      onProgress(done, total);
    }

    const blob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${folderName}.zip`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 15000);
  }

  return true;
}
